import { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { buildApiUrl } from '../config'
import { useAppStore } from '../stores/appStore'

interface LegType {
  id: number
  title: string
  order: number
  legs: Leg[]
}

interface Leg {
  id: number
  title: string
  slug: string
  height: number
  edge_inset?: number
  three_d_model: any
  texture_options: any[]
  product_thumbnail?: string
}

export default function HierarchicalLegSelector({ bedId }: { bedId: string }) {
  const { t } = useTranslation()
  const { market, lang, setBedComponent, bedConfig } = useAppStore()
  const [legTypes, setLegTypes] = useState<Record<string, LegType>>({})
  const [loading, setLoading] = useState(true)
  const [currentStep, setCurrentStep] = useState(1)
  const [selectedType, setSelectedType] = useState<string | null>(null)
  const [selectedHeight, setSelectedHeight] = useState<number | null>(null)
  const [selectedModel, setSelectedModel] = useState<string | null>(null)
  const [selectedMaterial, setSelectedMaterial] = useState<string | null>(null)
  const [selectedLeg, setSelectedLeg] = useState<Leg | null>(null)

  // Load leg types and legs
  useEffect(() => {
    const loadLegs = async () => {
      try {
        // Get bed width from the selected size, fallback to current model width, then default to 180cm
        const bedWidth = bedConfig.bed.selectedSize || 
          (bedConfig.bed.currentModel?.width) || 180
        
        const response = await fetch(buildApiUrl(`bed-variations/${bedId}/legs/?market=${market}&lang=${lang}&width_cm=${bedWidth}`))
        const data = await response.json()
        setLegTypes(data.leg_types || {})
      } catch (error) {
        console.error('Failed to load legs:', error)
      } finally {
        setLoading(false)
      }
    }

    if (bedId) {
      loadLegs()
    }
  }, [bedId, market, lang, bedConfig.bed.selectedSize, bedConfig.bed.currentModel?.width])

  // Initialize state based on current leg selection
  useEffect(() => {
    if (legTypes && Object.keys(legTypes).length > 0 && bedConfig.legs) {
      const currentLeg = bedConfig.legs
      
      // Find the leg type that contains the current leg
      const legTypeTitle = Object.keys(legTypes).find(typeTitle => {
        const legs = legTypes[typeTitle]?.legs || []
        return legs.some(leg => leg.id === currentLeg.id)
      })
      
      if (legTypeTitle) {
        setSelectedType(legTypeTitle)
        setSelectedHeight(currentLeg.height)
        
        // Find the actual leg object
        const leg = legTypes[legTypeTitle].legs.find(leg => leg.id === currentLeg.id)
        if (leg) {
          setSelectedLeg(leg)
          // Extract model and material from leg title
          const parts = leg.title.split(',')
          setSelectedModel(parts[0].trim().split(' ')[0])
          setSelectedMaterial(parts[2]?.trim() || '')
        }
        
        // Set current step to the final step since we have a selection
        setCurrentStep(3)
      }
    }
  }, [legTypes, bedConfig.legs])

  // Helper functions to get available options
  const getHeights = (typeTitle: string): number[] => {
    const legs = legTypes[typeTitle]?.legs || []
    const heights = new Set<number>()
    
    legs.forEach(leg => {
      heights.add(leg.height)
    })
    
    return Array.from(heights).sort((a, b) => a - b)
  }

  const getModels = (typeTitle: string, height: number): string[] => {
    const legs = legTypes[typeTitle]?.legs || []
    const models = new Set<string>()
    
    legs.forEach(leg => {
      if (leg.height === height) {
        const parts = leg.title.split(',')
        if (parts.length > 0) {
          const modelPart = parts[0].trim()
          const modelName = modelPart.split(' ')[0]
          models.add(modelName)
        }
      }
    })
    
    return Array.from(models).sort()
  }

  const getMaterials = (typeTitle: string, height: number, modelName: string): string[] => {
    const legs = legTypes[typeTitle]?.legs || []
    const materials = new Set<string>()
    
    legs.forEach(leg => {
      if (leg.height === height) {
        const parts = leg.title.split(',')
        if (parts.length > 2) {
          const modelPart = parts[0].trim()
          const modelNameFromTitle = modelPart.split(' ')[0]
          
          if (modelNameFromTitle === modelName) {
            materials.add(parts[2].trim())
          }
        }
      }
    })
    
    return Array.from(materials).sort()
  }

  const getLegsForTypeAndHeight = (typeTitle: string, height: number): Leg[] => {
    const legs = legTypes[typeTitle]?.legs || []
    return legs.filter(leg => leg.height === height)
  }

  const findLeg = (typeTitle: string, height: number, modelName: string, material: string): Leg | null => {
    const legs = legTypes[typeTitle]?.legs || []
    return legs.find(leg => {
      if (leg.height === height) {
        const parts = leg.title.split(',')
        if (parts.length > 2) {
          const modelPart = parts[0].trim()
          const modelNameFromTitle = modelPart.split(' ')[0]
          return modelNameFromTitle === modelName && parts[2].trim() === material
        }
      }
      return false
    }) || null
  }

  // Handle selections
  const handleTypeSelect = (typeTitle: string) => {
    setSelectedType(typeTitle)
    setSelectedHeight(null)
    setSelectedModel(null)
    setSelectedMaterial(null)
    setSelectedLeg(null)
    setCurrentStep(2)
  }

  const handleHeightSelect = (height: number) => {
    setSelectedHeight(height)
    setSelectedModel(null)
    setSelectedMaterial(null)
    setSelectedLeg(null)
    setCurrentStep(3)
  }

  const handleLegSelect = (leg: Leg) => {
    setSelectedModel(leg.title.split(',')[0].trim().split(' ')[0])
    setSelectedMaterial(leg.title.split(',')[2]?.trim() || '')
    setSelectedLeg(leg)
    
    // Apply the leg directly
    const slots = (leg.texture_options || []) as Array<{ slot_name: string; slot_label: string; textures: any[] }>
    const firstSlot = slots[0]
    const textures = firstSlot?.textures || []
    
    // Get current bed width to find matching texture
    const bedWidth = bedConfig.bed.selectedSize || 
      (bedConfig.bed.currentModel?.width) || null
    
    // Find texture that matches current bed width (exact match preferred)
    let selectedTexture = textures[0] || null
    if (bedWidth !== null && textures.length > 0) {
      // First try exact width match
      const exactMatch = textures.find((t: any) => {
        const tWidth = t.width_cm
        const bedWidthNum = Number(bedWidth)
        return tWidth === bedWidthNum || tWidth === bedWidth || Number(tWidth) === bedWidthNum
      })
      if (exactMatch) {
        selectedTexture = exactMatch
      }
    }
    
    // Get the leg type ID from the current leg type
    const currentLegType = legTypes[selectedType!]
    
    setBedComponent('legs', {
      id: leg.id,
      name: leg.title,
      slug: leg.slug,
      height: leg.height,
      edge_inset: leg.edge_inset,
      legTypeId: currentLegType?.id, // Only store the leg type ID
      three_d_model: leg.three_d_model,
      texture_options: slots,
      currentTexture: selectedTexture,
    } as any)
  }

  const goToStep = (step: number) => {
    setCurrentStep(step)
  }

  if (loading) {
    return <div>{t('legs.loading')}</div>
  }

  // Sort leg types so '4 Legs' comes before '8 Legs', keep 'Bas' last; otherwise by order
  const typeTitles = Object.keys(legTypes).sort((a, b) => {
    const normalize = (s: string) => s.toLowerCase()
    const aN = normalize(a)
    const bN = normalize(b)
    // Put base last if present
    if (aN.includes('base') && !bN.includes('base')) return 1
    if (!aN.includes('base') && bN.includes('base')) return -1
    // Ensure 4 legs before 8 legs
    const aIs4 = /\b4\b/.test(aN) || aN.includes('4 legs')
    const bIs4 = /\b4\b/.test(bN) || bN.includes('4 legs')
    const aIs8 = /\b8\b/.test(aN) || aN.includes('8 legs')
    const bIs8 = /\b8\b/.test(bN) || bN.includes('8 legs')
    if (aIs4 && bIs8) return -1
    if (aIs8 && bIs4) return 1
    // Fallback to provided order field
    return (legTypes[a]?.order || 0) - (legTypes[b]?.order || 0)
  })

  if (typeTitles.length === 0) {
    return <div>{t('legs.noLegsAvailable')}</div>
  }

  return (
    <div className="px-4">
      {/* Step Navigation */}
      <div className="flex border-b border-gray-200 mb-6">
        {[
          { key: 1, label: t('legs.steps.type'), canGoTo: true },
          { key: 2, label: t('legs.steps.height'), canGoTo: !!selectedType },
          { key: 3, label: t('legs.steps.selectLeg'), canGoTo: !!selectedHeight }
        ].map(tab => (
          <div
            key={tab.key}
            onClick={() => tab.canGoTo && goToStep(tab.key)}
            className={`px-5 py-3 cursor-pointer font-medium ${
              currentStep === tab.key 
                ? 'border-b-2 border-brand text-brand' 
                : tab.canGoTo 
                  ? 'border-b-2 border-transparent text-gray-500 hover:text-gray-700' 
                  : 'border-b-2 border-transparent text-gray-300 cursor-not-allowed'
            }`}
          >
            {tab.label}
          </div>
        ))}
      </div>

      {/* Step 1: Leg Type */}
      {currentStep === 1 && (
        <div>
          <h3 className="text-lg font-medium mb-4">{t('legs.selectType')}</h3>
          <div className="flex bg-gray-100 rounded-full p-1 max-w-xs">
            {typeTitles.map(typeTitle => (
              <button
                key={typeTitle}
                onClick={() => handleTypeSelect(typeTitle)}
                className={`flex-1 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedType === typeTitle 
                    ? 'bg-brand text-white' 
                    : 'text-gray-700 hover:bg-gray-200'
                }`}
              >
                {typeTitle === '8 Legs' ? t('legs.eightLegs') : typeTitle === '4 Legs' ? t('legs.fourLegs') : t('legs.base')}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Height */}
      {currentStep === 2 && (
        <div>
          <h3 className="text-lg font-medium mb-4">{t('legs.selectHeight')}</h3>
          <div className="flex bg-gray-100 rounded-full p-1 max-w-xs">
            {getHeights(selectedType!).map(height => (
              <button
                key={height}
                onClick={() => handleHeightSelect(height)}
                className={`flex-1 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedHeight === height 
                    ? 'bg-brand text-white' 
                    : 'text-gray-700 hover:bg-gray-200'
                }`}
              >
                {height}cm
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 3: Leg Selection */}
      {currentStep === 3 && (
        <div>
          <h3 className="text-lg font-medium mb-4">{t('legs.selectLeg')}</h3>
          <div className="relative pb-2">
            {/* JS scroll controls overlay (outside scroller so it doesn't scroll away) */}
            <LegsScrollControls fullHeight paddingBottom />
            <div className="overflow-x-auto scroll-buttons">
              <div className="flex gap-4 w-max">
            {getLegsForTypeAndHeight(selectedType!, selectedHeight!).map(leg => {
              const isSelected = selectedLeg?.id === leg.id
              
              // Remove height from title (e.g., "Eicon Oak, 14 cm" -> "Eicon Oak" or "Konade ben White washed ek 14 cm" -> "Konade ben White washed ek")
              const displayTitle = leg.title.replace(/(, )?\d+ cm$/, '')
              
              return (
                <div
                data-id={leg.id}
                data-type="legs"
                  key={leg.id}
                  onClick={() => handleLegSelect(leg)}
                  className="flex-shrink-0 w-28 text-center cursor-pointer"
                >
                  {leg.product_thumbnail ? (
                    <img 
                      src={leg.product_thumbnail} 
                      alt={displayTitle}
                      className={`aspect-square w-28 mb-2 ${isSelected ? 'outline-selected' : ''}`}
                    />
                  ) : (
                    <div className={`aspect-square w-28 mb-2 bg-gray-200 flex items-center justify-center text-gray-500 text-xs ${isSelected ? 'outline-selected' : ''}`}>
                      {displayTitle.charAt(0)}
                    </div>
                  )}
                  <div className="text-center">
                    <h4 className="text-xs break-words max-w-28">{displayTitle}</h4>
                  </div>
                </div>
              )
            })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function LegsScrollControls({ fullHeight = false, paddingBottom = false }: { fullHeight?: boolean; paddingBottom?: boolean }) {
  const ref = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const parent = ref.current?.parentElement
    if (!parent) return
    const scroller = parent
    // Show by default before first measurement
    const leftBtnInit = parent.querySelector<HTMLButtonElement>('[data-scroll-left]')
    const rightBtnInit = parent.querySelector<HTMLButtonElement>('[data-scroll-right]')
    if (leftBtnInit) leftBtnInit.style.display = 'flex'
    if (rightBtnInit) rightBtnInit.style.display = 'flex'
    const update = () => {
      const atStart = scroller.scrollLeft <= 0
      const atEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 1
      const leftBtn = parent.querySelector<HTMLButtonElement>('[data-scroll-left]')
      const rightBtn = parent.querySelector<HTMLButtonElement>('[data-scroll-right]')
      const canScroll = scroller.scrollWidth - scroller.clientWidth > 1
      if (leftBtn) leftBtn.style.display = canScroll && !atStart ? 'flex' : 'none'
      if (rightBtn) rightBtn.style.display = canScroll && !atEnd ? 'flex' : 'none'
    }
    const ro = new ResizeObserver(() => update())
    ro.observe(scroller)
    requestAnimationFrame(update)
    setTimeout(update, 100)
    setTimeout(update, 300)
    scroller.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    return () => {
      scroller.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
      ro.disconnect()
    }
  }, [])
  const scrollByAmount = (dir: number) => {
    const parent = ref.current?.parentElement
    if (!parent) return
    const step = Math.max(160, Math.round(parent.clientWidth * 0.5))
    parent.scrollBy({ left: dir * step, behavior: 'smooth' })
  }
  return (
    <div ref={ref} className="pointer-events-none select-none">
      <button
        type="button"
        data-scroll-left
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); scrollByAmount(-1) }}
        className={`pointer-events-auto absolute left-0 w-8 ${fullHeight ? 'inset-y-0' : 'bottom-0 h-36'} ${paddingBottom ? 'pb-8' : ''} flex items-center justify-center bg-[#ebe7e1] z-20 text-2xl`}
      >
        ‹
      </button>
      <button
        type="button"
        data-scroll-right
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); scrollByAmount(1) }}
        className={`pointer-events-auto absolute right-0 w-8 ${fullHeight ? 'inset-y-0' : 'bottom-0 h-36'} ${paddingBottom ? 'pb-8' : ''} flex items-center justify-center bg-[#ebe7e1] z-20 text-2xl`}
      >
        ›
      </button>
    </div>
  )
}